import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

// Cache module-level partagé entre TOUTES les instances du combobox. Évite
// d'appeler /api/villes plusieurs fois en parallèle quand plusieurs adresses
// sont ouvertes en même temps. Le backend a déjà son propre cache TTL 1h.
let _villesCache = null;       // Array<string> | null
let _villesInflight = null;    // Promise<Array<string>> | null

const fetchVilles = async () => {
    if (_villesCache !== null) return _villesCache;
    if (_villesInflight) return _villesInflight;
    _villesInflight = (async () => {
        try {
            const { data } = await api.get("/villes");
            _villesCache = Array.isArray(data?.villes) ? data.villes : [];
            return _villesCache;
        } catch {
            _villesCache = []; // évite les retries en boucle
            return _villesCache;
        } finally {
            _villesInflight = null;
        }
    })();
    return _villesInflight;
};

/**
 * Combobox de sélection de ville avec recherche.
 * - Accepte une valeur libre (string) — l'utilisateur peut taper une ville hors liste
 *   et elle sera persistée telle quelle.
 * - Liste fournie par /api/villes (issue de Fvi.distance via UNION ville1/ville2).
 */
export const VilleCombobox = ({
    value = "", onChange, disabled, className,
    placeholder = "Sélectionner une ville…",
    testId = "ville-combobox",
}) => {
    const [open, setOpen] = useState(false);
    const [villes, setVilles] = useState(_villesCache || []);
    const [loading, setLoading] = useState(_villesCache === null);

    useEffect(() => {
        let alive = true;
        if (_villesCache === null) {
            fetchVilles().then((list) => {
                if (alive) {
                    setVilles(list);
                    setLoading(false);
                }
            });
        }
        return () => { alive = false; };
    }, []);

    const handleSelect = (selected) => {
        const matched = villes.find((v) => v.toLowerCase() === selected.toLowerCase());
        onChange(matched || selected);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "w-full justify-between font-normal text-sm",
                        !value && "text-muted-foreground",
                        className,
                    )}
                    data-testid={testId}
                >
                    <span className="truncate">{value || placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
                <Command
                    filter={(itemValue, search) => {
                        const norm = (s) => s.toLowerCase()
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                        return norm(itemValue).includes(norm(search)) ? 1 : 0;
                    }}
                >
                    <CommandInput placeholder="Rechercher une ville…" className="h-9" data-testid={`${testId}-search`} />
                    <CommandList>
                        {loading ? (
                            <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                Chargement des villes…
                            </div>
                        ) : (
                            <>
                                <CommandEmpty>Aucune ville trouvée.</CommandEmpty>
                                <CommandGroup>
                                    {villes.map((ville) => (
                                        <CommandItem
                                            key={ville}
                                            value={ville}
                                            onSelect={handleSelect}
                                            className="text-sm"
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-3 w-3",
                                                    value?.toLowerCase() === ville.toLowerCase()
                                                        ? "opacity-100" : "opacity-0",
                                                )}
                                            />
                                            {ville}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
